import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  Building2,
  CheckCircle2,
  FileText,
  Home,
  Library,
  LockKeyhole,
  LogOut,
  MessageSquareText,
  NotebookPen,
  Settings,
  Sparkles,
} from "lucide-react";
import { useAuth } from "./auth/AuthContext";
import { isSupabaseConfigured } from "./auth/supabaseClient";
import { createWorkerAIProvider } from "./ai/workerAIProvider";
import type { AssistantSuggestion, GenerateSuggestionsResult } from "./ai/AIProvider";
import { getAppEnvironment } from "./lib/env";
import type { NavKey, PolicyNote, ProductEntitlement } from "./lib/types";
import { SupabasePolicyRepository } from "./repositories/supabasePolicyRepository";
import type { PolicyRepository } from "./repositories/policyRepository";

const navItems: Array<{ key: NavKey; label: string; icon: typeof Home }> = [
  { key: "home", label: "ホーム", icon: Home },
  { key: "assistant", label: "政策秘書", icon: MessageSquareText },
  { key: "stock", label: "政策ストック", icon: NotebookPen },
  { key: "library", label: "ライブラリ", icon: Library },
  { key: "settings", label: "設定", icon: Settings },
];

const briefingSteps = ["自自治体の議会を確認", "行政資料を確認", "政策ノートと照合", "方向性を整理"];

export default function App() {
  const auth = useAuth();
  const [activeView, setActiveView] = useState<NavKey>("home");
  const repository = useMemo<PolicyRepository>(() => new SupabasePolicyRepository(), []);
  const environment = useMemo(() => getAppEnvironment(), []);

  if (!auth.configured) return <SetupScreen />;
  if (auth.loading) return <LoadingScreen />;
  if (!auth.user || !auth.session) return <SignInScreen onSignIn={auth.signInWithGoogle} />;

  return (
    <Workspace
      activeView={activeView}
      setActiveView={setActiveView}
      environment={environment}
      repository={repository}
      accessToken={auth.session.access_token}
      userId={auth.user.id}
      email={auth.user.email ?? ""}
      signOut={auth.signOut}
    />
  );
}

function Workspace({
  activeView,
  setActiveView,
  environment,
  repository,
  accessToken,
  userId,
  email,
  signOut,
}: {
  activeView: NavKey;
  setActiveView: (view: NavKey) => void;
  environment: ReturnType<typeof getAppEnvironment>;
  repository: PolicyRepository;
  accessToken: string;
  userId: string;
  email: string;
  signOut: () => Promise<void>;
}) {
  const [notes, setNotes] = useState<PolicyNote[]>([]);
  const [entitlement, setEntitlement] = useState<ProductEntitlement | null>(null);
  const [dataMessage, setDataMessage] = useState("");

  async function refresh() {
    try {
      const profile = await repository.getUserProfile(userId);
      if (!profile) {
        await repository.upsertUserProfile({ user_id: userId, display_name: email.split("@")[0] || null });
      }
      const [loadedNotes, loadedEntitlement] = await Promise.all([
        repository.listPolicyNotes(userId),
        repository.getEntitlement(userId, environment),
      ]);
      setNotes(loadedNotes);
      setEntitlement(loadedEntitlement);
      setDataMessage("");
    } catch {
      setDataMessage("Supabaseのスキーマ適用またはExposed schemasの設定を確認してください。");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <img src="/brand-symbol-color.png" alt="" />
          <div>
            <strong>政策eye</strong>
            <span>CityWide</span>
          </div>
        </div>
        <nav>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                key={item.key}
                className={activeView === item.key ? "active" : ""}
                onClick={() => setActiveView(item.key)}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <span className="mode-pill">
            <LockKeyhole size={14} />
            ローカル思想を維持
          </span>
          <small>CityWide IDで利用中</small>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <p className="eyebrow">AI政策秘書サービス</p>
            <h1>{titleFor(activeView)}</h1>
          </div>
          <div className="topbar-actions">
            <span className="security-badge">
              <LockKeyhole size={15} />
              クラウドモード
            </span>
            <button type="button" className="icon-button" onClick={() => void signOut()} aria-label="ログアウト">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {dataMessage ? <div className="setup-warning">{dataMessage}</div> : null}

        {activeView === "home" && <HomeView notes={notes} go={setActiveView} />}
        {activeView === "assistant" && (
          <AssistantView
            accessToken={accessToken}
            userId={userId}
            onSave={async (suggestion, result) => {
              await repository.createPolicyNote(userId, {
                title: result.title,
                body: `${result.understood}\n\nおすすめ: ${suggestion.title}\n${suggestion.summary}\n\n理由: ${suggestion.reason}`,
                category: "question_seed",
                sourceType: "assistant",
              });
              await refresh();
              setActiveView("stock");
            }}
          />
        )}
        {activeView === "stock" && <PolicyStockView notes={notes} />}
        {activeView === "library" && <LibraryView />}
        {activeView === "settings" && (
          <SettingsView email={email} userId={userId} environment={environment} entitlement={entitlement} />
        )}
      </main>
    </div>
  );
}

function titleFor(view: NavKey) {
  return {
    home: "ホーム",
    assistant: "政策秘書",
    stock: "政策ストック",
    library: "ライブラリ",
    settings: "設定",
  }[view];
}

function SetupScreen() {
  return (
    <div className="center-screen">
      <div className="setup-card">
        <img src="/brand-symbol-color.png" alt="" />
        <h1>政策eye</h1>
        <p>Supabaseの接続情報を `.env` に設定すると、CityWide IDでログインできます。</p>
        <code>VITE_SUPABASE_URL / VITE_SUPABASE_PUBLISHABLE_KEY</code>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="center-screen">
      <div className="loading-mark" />
      <p>政策秘書を準備しています</p>
    </div>
  );
}

function SignInScreen({ onSignIn }: { onSignIn: () => Promise<void> }) {
  const [error, setError] = useState("");
  return (
    <div className="public-page">
      <section className="public-copy">
        <div className="brand large">
          <img src="/brand-symbol-color.png" alt="" />
          <div>
            <strong>政策eye</strong>
            <span>地方議員のためのAI政策秘書</span>
          </div>
        </div>
        <h1>街と議会を知る政策秘書が、質問の方向性を持ってきます。</h1>
        <p>CityWide IDでログインし、政策ノート・議会質問案・発信下書きを同じアカウントに蓄積します。</p>
      </section>
      <section className="sign-in-panel">
        <span className="security-badge">
          <LockKeyhole size={15} />
          CityWide共通ID
        </span>
        <h2>政策eyeをはじめる</h2>
        <button
          type="button"
          className="primary-button"
          onClick={() => onSignIn().catch(() => setError("Googleログインを開始できませんでした。"))}
        >
          Googleでログイン
          <ArrowRight size={18} />
        </button>
        {error ? <p className="form-error">{error}</p> : null}
      </section>
    </div>
  );
}

function HomeView({ notes, go }: { notes: PolicyNote[]; go: (view: NavKey) => void }) {
  return (
    <div className="home-grid">
      <section className="hero-panel">
        <div className="secretary-portrait">
          <img src="/secretary-soft.png" alt="" />
        </div>
        <div>
          <p className="eyebrow">政策秘書から</p>
          <h2>今日は、どの政策を前に進めますか。</h2>
          <p>問題意識を話すだけで、議会・行政資料・政策ノートをもとに方向性を整理します。</p>
          <button type="button" className="primary-button" onClick={() => go("assistant")}>
            政策秘書に相談する
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <section className="quiet-section">
        <div className="section-heading">
          <h2>最近の政策ストック</h2>
          <button type="button" className="text-button" onClick={() => go("stock")}>
            すべて見る
          </button>
        </div>
        <div className="stack-list">
          {notes.length ? (
            notes.slice(0, 3).map((note) => <MiniItem key={note.id} title={note.title} body={note.body} />)
          ) : (
            <MiniItem title="公共交通の論点整理" body="過去答弁の進捗確認から入ると質問に新規性が出ます。" />
          )}
        </div>
      </section>

      <section className="quiet-section">
        <div className="section-heading">
          <h2>準備中の議会質問</h2>
        </div>
        <MiniItem title="地域公共交通の見直し" body="想定答弁と再質問の整理待ちです。" />
      </section>
    </div>
  );
}

function AssistantView({
  accessToken,
  userId,
  onSave,
}: {
  accessToken: string;
  userId: string;
  onSave: (suggestion: AssistantSuggestion, result: GenerateSuggestionsResult) => Promise<void>;
}) {
  const ai = useMemo(() => createWorkerAIProvider(), []);
  const [text, setText] = useState("");
  const [result, setResult] = useState<GenerateSuggestionsResult | null>(null);
  const [processing, setProcessing] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const recommended = result?.suggestions.find((suggestion) => suggestion.recommended) ?? result?.suggestions[0];
  const alternatives = result?.suggestions.filter((suggestion) => suggestion.id !== recommended?.id) ?? [];

  async function handleSubmit() {
    if (!text.trim()) return;
    setProcessing(true);
    setResult(null);
    setEvidenceOpen(false);
    setStepIndex(0);

    for (let index = 0; index < briefingSteps.length; index += 1) {
      setStepIndex(index);
      await new Promise((resolve) => window.setTimeout(resolve, 430));
    }

    const suggestions = await ai.generateSuggestions({ text, accessToken, userId });
    setResult(suggestions);
    setProcessing(false);
  }

  return (
    <div className="assistant-layout">
      <section className="conversation-panel">
        <div className="assistant-head">
          <img src="/secretary-soft.png" alt="" />
          <div>
            <p className="eyebrow">政策秘書</p>
            <h2>考えを聞かせてください。調べた上で、方向性を持ってきます。</h2>
          </div>
        </div>

        <label className="input-label" htmlFor="assistant-text">
          今回は何について考えていますか？
        </label>
        <textarea
          id="assistant-text"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="例：地域の公共交通について。バスが減って、高齢者だけでなく高校生の通学にも影響が出ている気がします。"
        />
        <div className="action-line">
          <button type="button" className="primary-button" onClick={handleSubmit} disabled={!text.trim() || processing}>
            相談する
            <ArrowRight size={18} />
          </button>
          <span>音声での相談にも対応予定です。</span>
        </div>
      </section>

      {processing ? (
        <section className="briefing-panel">
          <div className="working-scene">
            <div className="scene-town">
              <span className="town-dot hall">
                <Building2 size={20} />
              </span>
              <span className="town-dot library">
                <BookOpen size={20} />
              </span>
              <span className="town-dot note">
                <NotebookPen size={20} />
              </span>
              <span className="assistant-runner">
                <img src="/secretary-soft.png" alt="" />
              </span>
            </div>
            <p>{briefingSteps[stepIndex]}しています。</p>
          </div>
        </section>
      ) : null}

      {result && recommended ? (
        <section className="briefing-panel">
          <p className="eyebrow">政策秘書からのブリーフィング</p>
          <h2>お話をこう理解しました</h2>
          <p className="understood">{result.understood}</p>

          <div className="recommendation-card">
            <span className="label">おすすめ</span>
            <h3>{recommended.title}</h3>
            <p>{recommended.summary}</p>
            <div className="reason-box">
              <strong>理由</strong>
              <span>{recommended.reason}</span>
            </div>
            <div className="recommendation-actions">
              <button
                type="button"
                className="primary-button"
                disabled={saving}
                onClick={() => {
                  setSaving(true);
                  onSave(recommended, result).finally(() => setSaving(false));
                }}
              >
                この方向で残す
                <CheckCircle2 size={18} />
              </button>
              <button type="button" className="secondary-button" onClick={() => setEvidenceOpen((value) => !value)}>
                根拠を見る
              </button>
            </div>
            {evidenceOpen ? (
              <div className="evidence-list">
                {recommended.evidence.map((item) => (
                  <article key={`${item.label}-${item.title}`}>
                    <span>{item.label}</span>
                    <strong>{item.title}</strong>
                    <p>{item.excerpt}</p>
                  </article>
                ))}
              </div>
            ) : null}
          </div>

          <div className="alternatives">
            <p>別の方向</p>
            {alternatives.map((suggestion) => (
              <article key={suggestion.id}>
                <strong>{suggestion.title}</strong>
                <span>{suggestion.summary}</span>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function PolicyStockView({ notes }: { notes: PolicyNote[] }) {
  return (
    <div className="single-column">
      <section className="quiet-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">政策秘書との対話から生まれたもの</p>
            <h2>政策ストック</h2>
          </div>
        </div>
        <div className="stock-grid">
          {notes.length ? (
            notes.map((note) => <MiniItem key={note.id} title={note.title} body={note.body} />)
          ) : (
            <>
              <MiniItem title="政策ノート" body="相談から残した考えがここに蓄積されます。" />
              <MiniItem title="議会質問" body="質問通告案・想定答弁・再質問に育てます。" />
              <MiniItem title="ブログ・SNS" body="市民向けの発信下書きに展開します。" />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function LibraryView() {
  const shelves = [
    ["私の過去発言", "一般質問、発言録、過去原稿を保存します。", FileText],
    ["自自治体議会", "山口市議会など、自分の街の議事録を参照します。", Building2],
    ["行政資料", "総合計画、個別計画、予算書、地域資料を置きます。", BookOpen],
    ["他都市事例", "他自治体の質問・答弁・政策事例を蓄積します。", Library],
  ] as const;

  return (
    <div className="single-column">
      <section className="quiet-section">
        <p className="eyebrow">政策eyeが参照する知識の本棚</p>
        <h2>ライブラリ</h2>
        <div className="shelf-grid">
          {shelves.map(([title, body, Icon]) => (
            <article key={title} className="shelf-card">
              <Icon size={22} />
              <strong>{title}</strong>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function SettingsView({
  email,
  userId,
  environment,
  entitlement,
}: {
  email: string;
  userId: string;
  environment: string;
  entitlement: ProductEntitlement | null;
}) {
  return (
    <div className="single-column">
      <section className="quiet-section">
        <p className="eyebrow">CityWide ID</p>
        <h2>アカウントとセキュリティ</h2>
        <div className="settings-list">
          <Row label="メール" value={email} />
          <Row label="CityWide ID" value={userId} />
          <Row label="環境" value={environment} />
          <Row label="利用権" value={entitlement?.status ?? "未設定"} />
          <Row label="外部AI送信" value="必要な場合だけ、事前確認してから接続" />
        </div>
      </section>
    </div>
  );
}

function MiniItem({ title, body }: { title: string; body: string }) {
  return (
    <article className="mini-item">
      <strong>{title}</strong>
      <p>{body}</p>
    </article>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="settings-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
